import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Flex,
    Icon,
    Image,
    Button,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaCheckCircle, FaPlay, FaLock, FaArrowRight } from "react-icons/fa";
import { ChevronLeftIcon } from "@chakra-ui/icons";

const pulseGlow = keyframes`
    0%, 100% { box-shadow: 0 0 20px rgba(74, 144, 226, 0.3); }
    50% { box-shadow: 0 0 40px rgba(74, 144, 226, 0.6); }
`;

const checkmarkPop = keyframes`
    0% { transform: scale(0) rotate(-180deg); }
    50% { transform: scale(1.3) rotate(10deg); }
    70% { transform: scale(0.9) rotate(-5deg); }
    100% { transform: scale(1) rotate(0deg); }
`;

const checkGlow = keyframes`
    0%, 100% { filter: drop-shadow(0 0 4px rgba(34, 197, 94, 0.6)); }
    50% { filter: drop-shadow(0 0 12px rgba(34, 197, 94, 1)); }
`;

const timelineShine = keyframes`
    0% { background-position: 0% 0%; }
    50% { background-position: 100% 100%; }
    100% { background-position: 0% 0%; }
`;

const AllModulesOverview = () => {
    const navigate = useNavigate();
    const [hoveredModule, setHoveredModule] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const modules = [
        { 
            id: 1, 
            title: "Introduction", 
            status: "completed",
            description: "Welcome to your surgical preparation journey",
            fullDescription: "Get oriented with SurgReady and understand how this program will help you prepare for your upcoming surgery. Learn about the evidence-based approach we use.",
            image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80"
        },
        { 
            id: 2, 
            title: "Surgery and You", 
            status: "completed",
            description: "Understanding your procedure and what to expect",
            fullDescription: "Gain a comprehensive understanding of your specific procedure, what happens during surgery, and the recovery timeline you can expect.",
            image: "https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=600&q=80"
        },
        { 
            id: 3, 
            title: "Nutrition", 
            status: "in-progress",
            description: "Optimize your diet for faster healing",
            fullDescription: "Discover the essential nutrients your body needs before and after surgery. Learn meal planning strategies to support optimal healing and recovery.",
            image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=80"
        },
        { 
            id: 4, 
            title: "Supplementation", 
            status: "upcoming",
            description: "Evidence-based supplements for recovery",
            fullDescription: "Explore which supplements can support your surgical preparation and which ones to avoid. All recommendations are backed by clinical research.",
            image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=600&q=80"
        },
        { 
            id: 5, 
            title: "Physical Activity", 
            status: "upcoming",
            description: "Prehab exercises to improve outcomes",
            fullDescription: "Learn prehabilitation exercises designed to strengthen your body before surgery. Better fitness going in means faster recovery coming out.",
            image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&q=80"
        },
        { 
            id: 6, 
            title: "Mind-Body", 
            status: "upcoming",
            description: "Mental preparation and stress management",
            fullDescription: "Master relaxation techniques, visualization, and mindfulness practices to reduce anxiety and prepare mentally for your procedure.",
            image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80"
        },
        { 
            id: 7, 
            title: "Sleep", 
            status: "upcoming",
            description: "Optimize rest for healing and recovery",
            fullDescription: "Understand the critical role sleep plays in surgical recovery. Learn strategies to improve sleep quality in the weeks before your procedure.",
            image: "https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=600&q=80"
        },
        { 
            id: 8, 
            title: "Substances", 
            status: "upcoming",
            description: "Managing alcohol, tobacco, and other substances",
            fullDescription: "Learn how alcohol, tobacco, and other substances affect surgical outcomes and get support for making changes before your procedure.",
            image: "https://images.unsplash.com/photo-1569230919100-d3fd5e1132f4?w=600&q=80"
        },
        { 
            id: 9, 
            title: "Connection", 
            status: "upcoming",
            description: "Building your support network",
            fullDescription: "Discover the importance of social support in recovery. Learn how to communicate with loved ones and build your care team.",
            image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&q=80"
        },
        { 
            id: 10, 
            title: "Medication Management", 
            status: "upcoming",
            description: "Safely managing medications before surgery",
            fullDescription: "Review your current medications with guidance on which to continue, pause, or adjust before surgery for optimal safety.",
            image: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=600&q=80"
        },
        { 
            id: 11, 
            title: "Chronic Disease Management", 
            status: "upcoming",
            description: "Optimizing existing conditions pre-surgery",
            fullDescription: "If you have diabetes, heart disease, or other chronic conditions, learn how to optimize them before surgery for better outcomes.",
            image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&q=80"
        },
        { 
            id: 12, 
            title: "Day of Surgery", 
            status: "upcoming",
            description: "Final preparations and what to expect",
            fullDescription: "Your complete guide to surgery day. Know exactly what to do, what to bring, and what to expect from arrival to recovery.",
            image: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=600&q=80"
        },
    ];

    const handleModuleClick = (moduleId: number, title: string) => {
        if (title === "Sleep") {
            navigate("/module/sleep");
        } else {
            navigate(`/module/${moduleId}/1`);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "completed":
                return { icon: FaCheckCircle, color: "#22C55E", text: "Completed", bgColor: "rgba(34, 197, 94, 0.15)" };
            case "in-progress":
                return { icon: FaPlay, color: "#4A90E2", text: "In Progress", bgColor: "rgba(74, 144, 226, 0.15)" };
            case "upcoming":
                return { icon: FaLock, color: "whiteAlpha.400", text: "Upcoming", bgColor: "whiteAlpha.100" };
            default:
                return { icon: FaLock, color: "whiteAlpha.400", text: "Locked", bgColor: "whiteAlpha.100" };
        }
    };

    const completedCount = modules.filter(m => m.status === "completed").length;
    const inProgressCount = modules.filter(m => m.status === "in-progress").length;

    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <Box 
                bg="rgba(0, 0, 0, 0.9)"
                borderBottom="1px solid"
                borderColor="whiteAlpha.100"
                position="sticky"
                top={0}
                zIndex={100}
                backdropFilter="blur(20px)"
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "44px", md: "48px" }}
                    >
                        <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                            <Image 
                                src="/surgready/logo.png" 
                                alt="SurgReady" 
                                h={{ base: "44px", md: "52px" }}
                                objectFit="contain"
                            />
                        </HStack>
                        <HStack spacing={{ base: 4, md: 8 }}>
                            <Text
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="#4A90E2"
                            >
                                All Modules
                            </Text>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                            >
                                Community
                            </Button>
                            <HStack
                                spacing={2}
                                cursor="pointer"
                                onClick={() => navigate('/member-profile')}
                                _hover={{ opacity: 0.8 }}
                                transition="all 0.2s"
                            >
                                <Text
                                    fontSize="xs"
                                    fontWeight="400"
                                    letterSpacing="0.1em"
                                    textTransform="uppercase"
                                    color="whiteAlpha.900"
                                    display={{ base: "none", md: "block" }}
                                >
                                    I am SurgReady
                                </Text>
                                <Box
                                    bg="#4A90E2"
                                    borderRadius="full"
                                    w={8}
                                    h={8}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon as={FaUser} w={4} h={4} color="white" />
                                </Box>
                            </HStack>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="6xl" px={{ base: 4, md: 6 }} py={{ base: 8, md: 16 }}>
                <VStack align="flex-start" spacing={12}>
                    <HStack spacing={4}>
                        <Button
                            variant="ghost"
                            size="sm"
                            color="whiteAlpha.600"
                            _hover={{ color: "#4A90E2" }}
                            onClick={() => navigate('/member-homepage')}
                            px={0}
                        >
                            <ChevronLeftIcon w={5} h={5} mr={1} />
                            Back to Dashboard
                        </Button>
                    </HStack>

                    <VStack align="flex-start" spacing={4} w="full">
                        <Heading
                            fontSize={{ base: "3xl", md: "5xl" }}
                            fontWeight="400"
                            letterSpacing="-0.02em"
                        >
                            Your Learning Journey
                        </Heading>
                        <Text
                            fontSize={{ base: "md", md: "lg" }}
                            color="whiteAlpha.700"
                            maxW="600px"
                            lineHeight="1.7"
                        >
                            Complete each module to fully prepare for your surgery. Your progress is saved automatically.
                        </Text>
                        
                        {/* Progress Stats */}
                        <HStack spacing={6} mt={4}>
                            <HStack spacing={2} bg="whiteAlpha.100" borderRadius="full" px={4} py={2}>
                                <Text fontSize="xl" fontWeight="600" color="#4A90E2">{modules.length}</Text>
                                <Text fontSize="sm" color="whiteAlpha.600">Total</Text>
                            </HStack>
                            <HStack spacing={2} bg="rgba(34, 197, 94, 0.15)" borderRadius="full" px={4} py={2}>
                                <Text fontSize="xl" fontWeight="600" color="#22C55E">{completedCount}</Text>
                                <Text fontSize="sm" color="whiteAlpha.600">Completed</Text>
                            </HStack>
                            <HStack spacing={2} bg="rgba(74, 144, 226, 0.15)" borderRadius="full" px={4} py={2}>
                                <Text fontSize="xl" fontWeight="600" color="#4A90E2">{modules.length - completedCount - inProgressCount}</Text>
                                <Text fontSize="sm" color="whiteAlpha.600">Upcoming</Text>
                            </HStack>
                        </HStack>
                    </VStack>

                    {/* Timeline Container */}
                    <Box position="relative" w="full">
                        {/* Thick Shiny Blue Timeline Bar */}
                        <Box
                            position="absolute"
                            left={{ base: "20px", md: "50%" }}
                            transform={{ base: "none", md: "translateX(-50%)" }}
                            top={0}
                            bottom={0}
                            w={{ base: "12px", md: "14px" }}
                            bg="linear-gradient(180deg, #4A90E2 0%, #3DA2DD 25%, #4A90E2 50%, #22C55E 75%, #4A90E2 100%)"
                            backgroundSize="100% 200%"
                            animation={`${timelineShine} 4s ease-in-out infinite`}
                            borderRadius="full"
                            zIndex={0}
                            boxShadow="0 0 20px rgba(74, 144, 226, 0.5), 0 0 40px rgba(74, 144, 226, 0.3), inset 0 2px 4px rgba(255,255,255,0.3)"
                            _before={{
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: "50%",
                                transform: "translateX(-50%)",
                                w: "4px",
                                h: "100%",
                                bg: "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.4) 100%)",
                                borderRadius: "full"
                            }}
                        />

                        {/* Module Cards - Tighter Vertical Spacing */}
                        <VStack spacing={0} align="stretch" position="relative">
                            {modules.map((module, index) => {
                                const status = getStatusBadge(module.status);
                                const isEven = index % 2 === 0;
                                const isHovered = hoveredModule === module.id;
                                
                                return (
                                    <Flex
                                        key={module.id}
                                        direction={{ base: "row", md: isEven ? "row" : "row-reverse" }}
                                        align="flex-start"
                                        position="relative"
                                        py={1}
                                        my={-1}
                                    >
                                        {/* Timeline Node - Improved Checkmarks */}
                                        <Box
                                            position="absolute"
                                            left={{ base: "20px", md: "50%" }}
                                            transform={{ base: "translateX(-50%)", md: "translateX(-50%)" }}
                                            zIndex={2}
                                        >
                                            <Flex
                                                w={{ base: "40px", md: "44px" }}
                                                h={{ base: "40px", md: "44px" }}
                                                borderRadius="full"
                                                bg={module.status === "completed" 
                                                    ? "linear-gradient(135deg, #22C55E 0%, #16A34A 50%, #15803D 100%)" 
                                                    : module.status === "in-progress" 
                                                    ? "linear-gradient(135deg, #4A90E2 0%, #3B82F6 50%, #2563EB 100%)" 
                                                    : "#1a1a1a"}
                                                border="3px solid"
                                                borderColor={module.status === "completed" ? "#22C55E" : module.status === "in-progress" ? "#4A90E2" : "whiteAlpha.300"}
                                                alignItems="center"
                                                justifyContent="center"
                                                boxShadow={module.status === "completed" 
                                                    ? "0 0 20px rgba(34, 197, 94, 0.6), 0 4px 12px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.3)" 
                                                    : module.status === "in-progress" 
                                                    ? "0 0 20px rgba(74, 144, 226, 0.5)" 
                                                    : "0 2px 8px rgba(0,0,0,0.3)"}
                                                animation={module.status === "in-progress" 
                                                    ? `${pulseGlow} 2s ease-in-out infinite` 
                                                    : module.status === "completed" 
                                                    ? `${checkGlow} 2s ease-in-out infinite` 
                                                    : "none"}
                                                transition="all 0.3s ease"
                                            >
                                                {module.status === "completed" ? (
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        w="100%"
                                                        h="100%"
                                                    >
                                                        <svg 
                                                            width="22" 
                                                            height="22" 
                                                            viewBox="0 0 24 24" 
                                                            fill="none"
                                                            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}
                                                        >
                                                            <path 
                                                                d="M4 12l5 5L20 6" 
                                                                stroke="white" 
                                                                strokeWidth="3" 
                                                                strokeLinecap="round" 
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </Box>
                                                ) : (
                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="700"
                                                        color={module.status === "upcoming" ? "whiteAlpha.500" : "white"}
                                                    >
                                                        {String(index + 1).padStart(2, '0')}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Box>

                                        {/* Card Container - Compact */}
                                        <Box
                                            w={{ base: "calc(100% - 70px)", md: "44%" }}
                                            ml={{ base: "50px", md: isEven ? 0 : "auto" }}
                                            mr={{ base: 0, md: isEven ? "auto" : 0 }}
                                            pl={{ base: 3, md: isEven ? 0 : 6 }}
                                            pr={{ base: 0, md: isEven ? 6 : 0 }}
                                        >
                                            <Box
                                                bg={isHovered ? "whiteAlpha.100" : "whiteAlpha.50"}
                                                borderRadius="xl"
                                                overflow="hidden"
                                                border="1px solid"
                                                borderColor={isHovered ? "#4A90E2" : module.status === "in-progress" ? "#4A90E2" : "whiteAlpha.100"}
                                                cursor="pointer"
                                                onMouseEnter={() => setHoveredModule(module.id)}
                                                onMouseLeave={() => setHoveredModule(null)}
                                                transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                                                transform={isHovered ? "scale(1.03) translateY(-4px)" : "scale(1) translateY(0)"}
                                                boxShadow={isHovered 
                                                    ? "0 20px 40px rgba(74, 144, 226, 0.25), 0 10px 20px rgba(0,0,0,0.3)" 
                                                    : "0 2px 8px rgba(0,0,0,0.2)"}
                                                position="relative"
                                                zIndex={isHovered ? 10 : 1}
                                            >
                                                {/* Image - Normal Height */}
                                                <Box position="relative" overflow="hidden">
                                                    <Image
                                                        src={module.image}
                                                        alt={module.title}
                                                        w="full"
                                                        h={{ base: "100px", md: "120px" }}
                                                        objectFit="cover"
                                                        opacity={module.status === "upcoming" ? 0.5 : 1}
                                                        transition="all 0.4s ease"
                                                        transform={isHovered ? "scale(1.05)" : "scale(1)"}
                                                    />
                                                    <Box
                                                        position="absolute"
                                                        bottom={0}
                                                        left={0}
                                                        right={0}
                                                        h="80%"
                                                        bgGradient="linear(to-t, rgba(0,0,0,0.95), transparent)"
                                                    />
                                                    
                                                    {/* Status Badge on Image */}
                                                    <Box position="absolute" top={2} left={2}>
                                                        <HStack 
                                                            spacing={1} 
                                                            bg={status.bgColor}
                                                            backdropFilter="blur(10px)"
                                                            borderRadius="full" 
                                                            px={2} 
                                                            py={0.5}
                                                        >
                                                            <Icon as={status.icon} w={2.5} h={2.5} color={status.color} />
                                                            <Text
                                                                fontSize="2xs"
                                                                fontWeight="500"
                                                                color={status.color}
                                                            >
                                                                {status.text}
                                                            </Text>
                                                        </HStack>
                                                    </Box>
                                                </Box>

                                                {/* Content */}
                                                <VStack align="flex-start" p={4} spacing={2}>
                                                    <Heading
                                                        fontSize={{ base: "sm", md: "md" }}
                                                        fontWeight="500"
                                                        letterSpacing="-0.01em"
                                                        color={module.status === "upcoming" ? "whiteAlpha.600" : "white"}
                                                        transition="all 0.3s ease"
                                                    >
                                                        {module.title}
                                                    </Heading>
                                                    
                                                    {/* Description */}
                                                    <Text
                                                        fontSize={{ base: "xs", md: "sm" }}
                                                        color="whiteAlpha.700"
                                                        lineHeight="1.5"
                                                        noOfLines={isHovered ? 3 : 2}
                                                        transition="all 0.3s ease"
                                                    >
                                                        {isHovered ? module.fullDescription : module.description}
                                                    </Text>

                                                    {/* Start Button - Shows on hover */}
                                                    <Box
                                                        opacity={isHovered ? 1 : 0}
                                                        transform={isHovered ? "translateY(0)" : "translateY(5px)"}
                                                        transition="all 0.3s ease"
                                                        w="full"
                                                        mt={1}
                                                    >
                                                        <Button
                                                            w="full"
                                                            bg="linear-gradient(135deg, #4A90E2 0%, #3DA2DD 100%)"
                                                            color="white"
                                                            size="sm"
                                                            borderRadius="md"
                                                            fontWeight="600"
                                                            fontSize="sm"
                                                            letterSpacing="0.03em"
                                                            rightIcon={<Icon as={FaArrowRight} w={3} h={3} />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleModuleClick(module.id, module.title);
                                                            }}
                                                            _hover={{
                                                                bg: "linear-gradient(135deg, #357ABD 0%, #2B8BC4 100%)",
                                                                transform: "scale(1.02)"
                                                            }}
                                                            transition="all 0.2s ease"
                                                            boxShadow="0 2px 8px rgba(74, 144, 226, 0.4)"
                                                        >
                                                            <HStack spacing={2}>
                                                                <Icon as={FaPlay} w={3} h={3} />
                                                                <Text>Start Module</Text>
                                                            </HStack>
                                                        </Button>
                                                    </Box>
                                                </VStack>
                                            </Box>
                                        </Box>
                                    </Flex>
                                );
                            })}
                        </VStack>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
};

export default AllModulesOverview;
